

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, Wallet, Scale } from 'lucide-react';
import { formatCurrency } from '@/lib/data';
import type { DashboardMoney } from '../../useDashboard';

interface FinancialWidgetProps {
  money: DashboardMoney;
}

/**
 * Money that actually moved this month, by payment date: what customers paid us,
 * what we spent (day-to-day expenses + vendor payments) and the difference.
 * Expenses leave out the automatic copies of vendor payments, so nothing is counted twice.
 */
export function FinancialWidget({ money }: FinancialWidgetProps) {
  const net = money.month_net_cash;

  const items = [
    {
      label: 'Received',
      sublabel: `${money.month_payments_count} customer payment${money.month_payments_count === 1 ? '' : 's'}`,
      value: money.month_received,
      icon: <ArrowDownLeft className="w-5 h-5" />,
      color: 'text-green-600',
      bg: 'bg-green-50',
      to: '/client-payments',
    },
    {
      label: 'Expenses',
      sublabel: 'day-to-day spending',
      value: money.month_expenses,
      icon: <Wallet className="w-5 h-5" />,
      color: 'text-red-600',
      bg: 'bg-red-50',
      to: '/expenses',
    },
    {
      label: 'Paid to vendors',
      sublabel: 'vendor payments',
      value: money.month_vendor_payments,
      icon: <ArrowUpRight className="w-5 h-5" />,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      to: '/vendor-payments',
    },
    {
      label: 'Net cash',
      sublabel: 'received − spent',
      value: net,
      icon: <Scale className="w-5 h-5" />,
      color: net >= 0 ? 'text-green-600' : 'text-red-600',
      bg: net >= 0 ? 'bg-green-50' : 'bg-red-50',
      to: undefined,
    },
  ];

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-4">
        <h3 className="font-semibold text-stone-800">Money in and out</h3>
        <span className="text-xs text-stone-500">This month, by payment date</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map((item) => {
          const inner = (
            <>
              <div className={`${item.color} mb-2`}>{item.icon}</div>
              <p className="text-xs font-medium text-stone-700">{item.label}</p>
              <p className="text-[11px] text-stone-500 mb-1">{item.sublabel}</p>
              <p className={`text-sm sm:text-base font-bold tabular-nums ${item.value < 0 ? 'text-red-700' : 'text-stone-800'}`}>
                {formatCurrency(item.value)}
              </p>
            </>
          );
          return item.to ? (
            <Link key={item.label} to={item.to} className={`${item.bg} block rounded-xl p-3 min-h-[40px] border border-transparent hover:border-stone-300 transition-colors`}>
              {inner}
            </Link>
          ) : (
            <div key={item.label} className={`${item.bg} rounded-xl p-3`}>
              {inner}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-stone-500">
        Spent this month: <span className="font-semibold text-stone-700 tabular-nums">{formatCurrency(money.month_spent)}</span>.
        {' '}This is cash, not event profit — profit is in the tiles above.
      </p>
    </div>
  );
}
